import {useOnMount} from 'shared/web';

import {useEagGridStore} from '../store/useEagGridStore';
import type {ActionCallback} from '../types/ActionCallback';
import type {RowData} from '../types/RowData';
import {executeBulkAction} from '../utils/executeBulkAction';
import {INVOKE_EAGRID_BULK_ACTION_EVENT} from '../utils/invokeDatagridBulkAction';
import {useLatestRef} from './useLatestRef';

type UseExternalBulkActionInvocationParams = {
  gridCode: string;
  rows: RowData[];
  actionCallback: ActionCallback<readonly string[]> | undefined;
  refreshData: () => void;
};

/**
 * Listens for invokeDatagridBulkAction events targeting this grid and runs the action
 * through the grid's actionCallback via the shared executeBulkAction core — identical
 * behavior to the in-grid BulkActionsBar, but with rowIds supplied by the caller.
 * Subscribes once for the component's lifetime (useOnMount); the listener reads live
 * params from a ref so rows/actionCallback changes never re-subscribe.
 */
export const useExternalBulkActionInvocation = (
  params: UseExternalBulkActionInvocationParams
): void => {
  const store = useEagGridStore();
  const paramsRef = useLatestRef(params);

  const listener = (event: WindowEventMap[typeof INVOKE_EAGRID_BULK_ACTION_EVENT]) => {
    const current = paramsRef.current;
    if (event.detail.gridCode !== current.gridCode) {
      return;
    }
    executeBulkAction({
      store,
      actionCallback: current.actionCallback,
      rows: current.rows,
      rowIds: event.detail.target.rowIds,
      refreshData: current.refreshData,
      actionKey: event.detail.target.actionKey,
    });
  };

  useOnMount(
    () => window.addEventListener(INVOKE_EAGRID_BULK_ACTION_EVENT, listener),
    () => window.removeEventListener(INVOKE_EAGRID_BULK_ACTION_EVENT, listener)
  );
};
